import { useEffect, useRef, useState } from "react";
import { config } from "../config";
import { marketAbi, subnetRegistryAbi } from "../abi";
import { l2Chain, l2Public, walletFor } from "../lib/chain";
import { runInference, listMiners, type DiscoveredMiner } from "../lib/inference";
import { fmtToken, parseToken, shortAddr } from "../lib/format";
import { useWallet } from "../wallet";
import { Card, Field, Notice, Stat, Badge, cleanError } from "../components/ui";

interface Subnet {
  owner: `0x${string}`;
  modelURI: string;
  active: boolean;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  meta?: { charged: bigint; miner: string; settled: boolean; tokensIn: number; tokensOut: number };
}

/** Flatten the transcript to the wire prompt — same speaker-labelled convention as the SDK sidecar,
 *  so browser chats and agent chats hit miners identically. */
function flatten(messages: ChatMessage[]): string {
  return [...messages.map((m) => `${m.role}: ${m.content}`), "assistant:"].join("\n");
}

export function Inference() {
  const { account } = useWallet();
  const [brainId, setBrainId] = useState(7);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [budget, setBudget] = useState("0.05");
  const [subnet, setSubnet] = useState<Subnet | null>(null);
  const [miners, setMiners] = useState<DiscoveredMiner[] | null>(null);
  const [spending, setSpending] = useState<bigint | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string>();
  const chatEnd = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ok = true;
    setSubnet(null);
    setMiners(null);
    (async () => {
      try {
        const sn = (await l2Public.readContract({
          address: config.l2.contracts.subnetRegistry,
          abi: subnetRegistryAbi,
          functionName: "getSubnet",
          args: [BigInt(brainId)],
        })) as unknown as Subnet;
        if (ok) setSubnet(sn);
        const list = await listMiners(brainId);
        if (ok) setMiners([...list].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)));
      } catch {
        /* not configured / RPC down — leave nulls */
      }
    })();
    return () => {
      ok = false;
    };
  }, [brainId]);

  useEffect(() => {
    if (!account) return;
    l2Public
      .readContract({ address: config.l2.contracts.market, abi: marketAbi, functionName: "balances", args: [account] })
      .then((b) => setSpending(b as bigint))
      .catch(() => setSpending(null));
  }, [account, messages]);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, running]);

  async function send() {
    setError(undefined);
    if (!account) {
      setError("Connect your wallet first.");
      return;
    }
    const wallet = walletFor(l2Chain, account);
    if (!wallet) {
      setError("No injected wallet available.");
      return;
    }
    const userMsg: ChatMessage = { role: "user", content: draft.trim() };
    const transcript = [...messages, userMsg];
    setMessages(transcript);
    setDraft("");
    setRunning(true);
    try {
      const budgetWei = parseToken(budget);
      let reservation = 0n;
      try {
        reservation = (await l2Public.readContract({
          address: config.l2.contracts.market,
          abi: marketAbi,
          functionName: "maxReservation",
        })) as bigint;
      } catch {
        /* leave 0 -> runInference falls back to budget */
      }
      const res = await runInference({
        wallet,
        user: account,
        brainId,
        prompt: flatten(transcript),
        budget: budgetWei,
        reservation: reservation > 0n && reservation < budgetWei ? reservation : budgetWei,
      });
      setMessages([
        ...transcript,
        {
          role: "assistant",
          content: res.text,
          meta: {
            charged: res.charged,
            miner: res.miner,
            settled: res.settled,
            tokensIn: res.tokensIn,
            tokensOut: res.tokensOut,
          },
        },
      ]);
    } catch (e) {
      setError(cleanError(e));
    } finally {
      setRunning(false);
    }
  }

  const insufficient = spending !== null && spending < parseToken(budget || "0");
  const totalSpent = messages.reduce((s, m) => s + (m.meta?.charged ?? 0n), 0n);

  return (
    <>
      <div className="section-title">Inference</div>
      <div className="grid cols-2">
        <Card
          title="Chat"
          sub={`Brain #${brainId}`}
          right={
            messages.length > 0 ? (
              <button className="btn ghost sm" onClick={() => { setMessages([]); setError(undefined); }}>
                New chat
              </button>
            ) : undefined
          }
        >
          <div className="chat">
            {messages.length === 0 && (
              <div className="muted" style={{ textAlign: "center", padding: "28px 12px", fontSize: 14 }}>
                Ask the network anything. Each reply is served by a scored miner, hash-verified, and
                paid at the metered byte price — the full transcript is sent each turn.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`msg ${m.role}`}>
                <div className="msg-body">{m.content}</div>
                {m.meta && (
                  <div className="msg-meta">
                    {fmtToken(m.meta.charged)} BASED · {shortAddr(m.meta.miner as `0x${string}`)} ·{" "}
                    {m.meta.settled ? "settled" : "pre-auth only"} · {m.meta.tokensIn}/{m.meta.tokensOut} tok
                  </div>
                )}
              </div>
            ))}
            {running && (
              <div className="msg assistant">
                <div className="msg-body"><span className="spin" /> thinking…</div>
              </div>
            )}
            <div ref={chatEnd} />
          </div>

          <div className="row" style={{ marginTop: 12, alignItems: "flex-end" }}>
            <textarea
              className="textarea"
              style={{ minHeight: 54, flex: 1 }}
              placeholder="Message the Brain…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && draft.trim() && !running && account) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <button className="btn" onClick={() => void send()} disabled={running || !draft.trim() || !account}>
              Send
            </button>
          </div>
          {insufficient && (
            <div style={{ marginTop: 10 }}>
              <Notice kind="warn">
                Your spending balance ({fmtToken(spending!)} BASED) is below the budget. Top up on the
                Account page.
              </Notice>
            </div>
          )}
          {error && (
            <div style={{ marginTop: 10 }}>
              <Notice kind="error">{error}</Notice>
            </div>
          )}
        </Card>

        <div className="stack">
          <Card title="Brain status" right={<a href={`#/brains/${brainId}`} style={{ fontSize: 13 }}>explorer →</a>}>
            <div className="stack">
              <Field label="Brain ID" hint="0–63. Public Brains start at 7; browse them in the explorer.">
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={63}
                  value={brainId}
                  onChange={(e) => setBrainId(Math.max(0, Math.min(63, Number(e.target.value))))}
                  style={{ maxWidth: 140 }}
                />
              </Field>
              <Field label="Budget per message ($basedAI)" hint="Ceiling for each reply's metered charge.">
                <input className="input" value={budget} onChange={(e) => setBudget(e.target.value)} style={{ maxWidth: 140 }} />
              </Field>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <span className="muted">Brain</span>
                {subnet ? (
                  subnet.active ? <Badge kind="ok">Active</Badge> : <Badge kind="warn">Inactive</Badge>
                ) : (
                  <span className="muted">—</span>
                )}
              </div>
              <Stat label="Model" value={<span className="mono" style={{ fontSize: 13 }}>{subnet?.modelURI || "—"}</span>} />
              <div className="grid cols-2">
                <Stat label="Your spending balance" value={spending !== null ? fmtToken(spending) : "—"} unit="BASED" />
                <Stat label="Spent this chat" value={fmtToken(totalSpent)} unit="BASED" />
              </div>
            </div>
          </Card>

          <Card title="Miners" sub="Requests route to the highest-scored miner.">
            {miners === null ? (
              <div className="muted">—</div>
            ) : miners.length === 0 ? (
              <Notice kind="warn">No miners announced on this Brain — requests will fail.</Notice>
            ) : (
              <div className="stack" style={{ gap: 8 }}>
                {miners.slice(0, 5).map((m, i) => (
                  <div key={m.address} className="row" style={{ justifyContent: "space-between" }}>
                    <span className="mono" style={{ fontSize: 13 }}>
                      {i === 0 ? "→ " : ""}
                      {shortAddr(m.address)}
                    </span>
                    <span className="muted" style={{ fontSize: 13 }}>
                      score {m.score !== undefined ? m.score.toFixed(3) : "—"}
                    </span>
                  </div>
                ))}
                {miners.length > 5 && <div className="hint">+ {miners.length - 5} more</div>}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
